import React, { useState, useMemo } from "react";
import { X, Calculator } from "lucide-react";

const SHAPES = ["Rectangle", "Round", "Oval", "Kidney", "L Shape"];
const FT = 0.3048;

// Fields needed per shape (metres or feet depending on unit)
const FIELDS = {
  Rectangle: [["length", "Length"], ["width", "Width"]],
  Round: [["diameter", "Diameter"]],
  Oval: [["length", "Length"], ["width", "Width"]],
  Kidney: [["length", "Main length"], ["width", "Main width"]],
  "L Shape": [["length", "Main length"], ["width", "Main width"], ["length2", "Extra length"], ["width2", "Extra width"]],
};

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

function computeLitres(shape, d, unit) {
  const k = unit === "Feet" ? FT : 1;
  const L = num(d.length) * k, W = num(d.width) * k, D = num(d.diameter) * k;
  const L2 = num(d.length2) * k, W2 = num(d.width2) * k;
  const depth = ((num(d.shallow) + num(d.deep)) / 2) * k;
  let area = 0;
  if (shape === "Rectangle") area = L * W;
  else if (shape === "Round") area = Math.PI * (D / 2) * (D / 2);
  else if (shape === "Oval") area = Math.PI * (L / 2) * (W / 2);
  else if (shape === "Kidney") area = L * W * 0.85;
  else if (shape === "L Shape") area = L * W + L2 * W2;
  const m3 = area * depth;
  return { litres: Math.round(m3 * 1000), area, depth };
}

export default function VolumeCalculator({ initialLitres, onUse, onClose, mobile }) {
  const [unit, setUnit] = useState("Metres");
  const [shape, setShape] = useState("Rectangle");
  const [d, setD] = useState({ shallow: "", deep: "" });
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const { litres, area, depth } = useMemo(() => computeLitres(shape, d, unit), [shape, d, unit]);
  const Lbl = ({ children }) => <label className={`block ${mobile ? "text-[13px]" : "text-[11px]"} font-black text-[#123a63] mb-0.5`}>{children} ({unit === "Feet" ? "ft" : "m"})</label>;
  const inp = `w-full rounded-lg border border-[#7ba7d9] bg-white px-2.5 ${mobile ? "py-2 text-[16px]" : "py-1.5 text-[13px]"} text-[#123a63] outline-none focus:border-[#123a63]`;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm" data-testid="volume-calculator-modal" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white border-2 border-[#5b8ac4] shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-[#0e7490] to-[#123a63] text-white">
          <div className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            <div>
              <h2 className="font-display font-black text-base leading-tight">Pool Water Volume Calculator</h2>
              <p className="text-[10px] text-white/80 -mt-0.5">Glamour Pools and Spas</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="volume-calc-close" className="text-white/80 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-3">
          <div className="flex items-center gap-4">
            <span className="text-[11px] font-black text-[#123a63]">Measurement unit</span>
            <div className="flex rounded-lg overflow-hidden border border-[#7ba7d9]">
              {["Metres", "Feet"].map((u) => (
                <button key={u} onClick={() => setUnit(u)} data-testid={`volume-unit-${u.toLowerCase()}`}
                  className={`px-3 py-1 text-[12px] font-bold ${unit === u ? "bg-[#123a63] text-white" : "bg-white text-[#123a63] hover:bg-[#eef3fb]"}`}>{u}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-black text-[#123a63] mb-0.5">Pool shape</label>
            <div className="flex flex-wrap gap-1.5">
              {SHAPES.map((s) => (
                <button key={s} onClick={() => setShape(s)} data-testid={`volume-shape-${s.replace(/\s/g, "-").toLowerCase()}`}
                  className={`px-3 py-1 rounded-full text-[12px] font-bold border ${shape === s ? "bg-[#0e7490] text-white border-[#0e7490]" : "bg-white text-[#123a63] border-[#7ba7d9] hover:bg-[#eef3fb]"}`}>{s}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {FIELDS[shape].map(([k, label]) => (
              <div key={k}>
                <Lbl>{label}</Lbl>
                <input value={d[k] || ""} onChange={(e) => set(k, e.target.value)} inputMode="decimal" className={inp} data-testid={`volume-field-${k}`} />
              </div>
            ))}
            <div><Lbl>Shallow depth</Lbl><input value={d.shallow} onChange={(e) => set("shallow", e.target.value)} inputMode="decimal" className={inp} data-testid="volume-field-shallow" /></div>
            <div><Lbl>Deep depth</Lbl><input value={d.deep} onChange={(e) => set("deep", e.target.value)} inputMode="decimal" className={inp} data-testid="volume-field-deep" /></div>
          </div>

          <div className="rounded-xl bg-[#eaf6fb] border border-[#7ba7d9] p-3 text-center">
            <div className="text-[11px] font-bold text-[#0e7490] uppercase tracking-widest">Estimated pool water volume</div>
            <div className="text-3xl font-black text-[#123a63]" data-testid="volume-result-litres">{litres.toLocaleString()} <span className="text-base">litres</span></div>
            <div className="flex justify-center gap-5 mt-1 text-[11px] text-slate-600">
              <span><b>{(litres / 1000).toFixed(2)}</b> kL</span>
              <span>Avg depth <b>{depth.toFixed(2)}</b> m</span>
              <span>Surface <b>{area.toFixed(2)}</b> m²</span>
            </div>
          </div>
          <p className="text-[10px] text-slate-400 text-center">This is an estimate. Freeform and kidney-shaped pools may vary depending on the exact curve and design.</p>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} data-testid="volume-calc-cancel" className="rounded-md border border-slate-300 bg-white px-4 py-2 text-[13px] font-bold hover:bg-slate-100">Cancel</button>
          <button onClick={() => { onUse(litres); onClose(); }} disabled={!litres} data-testid="volume-calc-use"
            className="rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black text-[13px] px-6 py-2 shadow">Use This Volume</button>
        </div>
      </div>
    </div>
  );
}
